import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, useMediaQuery } from '@mui/material';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

export type NewPlanForm = {
    name: string;
    monthlyPricePerMonth: number | '';
    annualPricePerMonth: number | '';
    horizonDays: number | '';
    users: number | '';
};

type CreateSubscriptionPlanModalProps = {
    open: boolean;
    saving?: boolean;
    onClose: () => void;
    onCreate: (plan: NewPlanForm) => void;
};

const EMPTY: NewPlanForm = { name: '', monthlyPricePerMonth: '', annualPricePerMonth: '', horizonDays: 30, users: 1 };

// Admin > Subscription Tiers "Add plan". Creates the plan's Monthly and
// Yearly tiers together (express-server util/subscription-tiers.js
// createPlan); inclusions, add-ons and the rest are then set per tier with
// the normal Edit.
const CreateSubscriptionPlanModal: React.FC<CreateSubscriptionPlanModalProps> = ({ open, saving, onClose, onCreate }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState<NewPlanForm>(EMPTY);

    useEffect(() => {
        if (open) setForm(EMPTY);
    }, [open]);

    const num = (value: string) => (value === '' ? '' : Math.max(0, Number(value) || 0));
    const canCreate = !!form.name.trim() && Number(form.monthlyPricePerMonth) > 0 && Number(form.annualPricePerMonth) > 0;

    const content = (
        <>
            <DialogTitle variant="h6" fontWeight={600}>Add plan</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Plan name</Typography>
                        <TextField
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="e.g. Business"
                        />
                    </Box>
                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Monthly price per month ($)</Typography>
                            <TextField
                                type="number"
                                value={form.monthlyPricePerMonth}
                                onChange={e => setForm({ ...form, monthlyPricePerMonth: num(e.target.value) })}
                                size="small"
                                fullWidth
                                helperText="No commitment, billed monthly"
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Yearly price per month ($)</Typography>
                            <TextField
                                type="number"
                                value={form.annualPricePerMonth}
                                onChange={e => setForm({ ...form, annualPricePerMonth: num(e.target.value) })}
                                size="small"
                                fullWidth
                                helperText={Number(form.annualPricePerMonth) > 0
                                    ? `12 months up front: $${(Number(form.annualPricePerMonth) * 12).toLocaleString()}`
                                    : '12 months up front'}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Cash flow horizon (days)</Typography>
                            <TextField
                                type="number"
                                value={form.horizonDays}
                                onChange={e => setForm({ ...form, horizonDays: num(e.target.value) })}
                                size="small"
                                fullWidth
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Users</Typography>
                            <TextField
                                type="number"
                                value={form.users}
                                onChange={e => setForm({ ...form, users: num(e.target.value) })}
                                size="small"
                                fullWidth
                            />
                        </Box>
                    </Box>
                    <Typography fontSize={12} color="var(--color-text-muted)">
                        Creates the plan's Monthly and Yearly versions. Set what each one includes with Edit afterwards.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => onCreate(form)}
                    disabled={!canCreate || saving}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Add plan
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} heightVh={90}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default CreateSubscriptionPlanModal;
